import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sa } from '../superAdminUi';
import { formatByMode } from '../../utils/creditFormat';

// Dependency-free daily-usage bar chart for the Super Admin surface. Days arrive gap-filled
// (zero days included) so a whole month renders as a continuous run of teal bars, with quiet
// days shown as a baseline. The y-axis reads 0–100%: each bar is that day's credits as a
// percentage of the daily reference (the fair-share daily limit when known, otherwise the
// busiest day). The chart always plots credits; `mode` only sets the hover label's units.

const PLOT_HEIGHT = 200;
const GRID = [100, 80, 60, 40, 20, 0];

const Empty = styled.p`
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${sa.color.textGray};
  margin: 0;
  padding: 24px 0;
`;
const Wrap = styled.div`
  display: flex;
  width: 100%;
  font-family: ${sa.font.family};
`;
const YAxis = styled.div`
  width: 38px;
  height: ${PLOT_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding-right: 8px;
`;
const YLabel = styled.span`
  font-size: 10px;
  color: ${sa.color.textGray};
  line-height: 1;
`;
const PlotArea = styled.div`
  flex: 1 0 0;
  min-width: 0;
`;
const Plot = styled.div`
  position: relative;
  height: ${PLOT_HEIGHT}px;
`;
const GridLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: ${(p) => p.$top}%;
  border-top: 1px solid ${sa.color.strokeLight};
`;
const Bars = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  gap: 4px;
`;
const BarSlot = styled.div`
  flex: 1 1 0;
  min-width: 3px;
  height: 100%;
  display: flex;
  align-items: flex-end;
`;
const Bar = styled.div`
  width: 100%;
  height: ${(p) => p.$pct}%;
  min-height: 2px;
  background: ${(p) => (p.$zero ? sa.color.mist : sa.color.teal)};
  border-radius: ${sa.radius.sm} ${sa.radius.sm} 0 0;
  transition: height 0.2s;
`;
const XAxis = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;
`;
const XLabel = styled.span`
  flex: 1 1 0;
  min-width: 3px;
  text-align: center;
  font-size: 10px;
  color: ${sa.color.textGray};
`;

function DailyUsageChart({ days, mode, limit }) {
  if (!days.length) {
    return <Empty>No daily usage to chart for this period.</Empty>;
  }

  const reference =
    limit && limit > 0 ? limit : Math.max(...days.map((d) => d.credits), 1);
  const barPct = (credits) => Math.min(100, (credits / reference) * 100);
  const dayNumber = (date) => Number(date.slice(8));

  return (
    <Wrap>
      <YAxis>
        {GRID.map((g) => (
          <YLabel key={g}>{g}%</YLabel>
        ))}
      </YAxis>
      <PlotArea>
        <Plot>
          {GRID.map((g) => (
            <GridLine key={g} $top={100 - g} />
          ))}
          <Bars data-testid='daily-usage-chart'>
            {days.map((d) => (
              <BarSlot
                key={d.date}
                title={`${d.date}: ${formatByMode(d.credits, mode)}`}
              >
                <Bar $pct={barPct(d.credits)} $zero={d.credits <= 0} />
              </BarSlot>
            ))}
          </Bars>
        </Plot>
        <XAxis>
          {days.map((d) => {
            const n = dayNumber(d.date);
            return <XLabel key={d.date}>{n === 1 || n % 5 === 0 ? n : ''}</XLabel>;
          })}
        </XAxis>
      </PlotArea>
    </Wrap>
  );
}

DailyUsageChart.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({ date: PropTypes.string, credits: PropTypes.number })
  ).isRequired,
  mode: PropTypes.oneOf(['credit', 'cost', 'toggle']),
  limit: PropTypes.number,
};

DailyUsageChart.defaultProps = { mode: 'credit' };

export default DailyUsageChart;
